import {
  INPUT_TEXT_MAX_CHARS,
  personEventSchema,
  questionIdSchema,
  tapeEntrySchema,
} from "@typedoc/interview/events";
import { z } from "zod";
import { queryInteger } from "./chat";
import { idSchema } from "./ids";

/**
 * The vocabulary of the Recordings API (ENG-1015): a recording on a chat and its interview
 * session's tape. The tape is what leaves the server: a reader folds it with `replay` from
 * `@typedoc/interview/session`, the same transition the server runs, so the island and the API
 * agree on every question, receipt and say intent without a second shape for the session.
 */

/** Mirrors the session's mode once it has started: live, paused, finished, or stopped at the limit. */
export const RECORDING_STATES = ["live", "paused", "finished", "stopped"] as const;
export const recordingStateSchema = z.enum(RECORDING_STATES);
export type RecordingState = z.infer<typeof recordingStateSchema>;

export const RECORDING_INTENT_MAX_CHARS = 200;

/**
 * What a grant for one recording opens. `capture`: the desktop app adds screens (ENG-1024). `voice`:
 * the voice worker follows what to say and reports what it heard (ENG-1017). Both live in one table
 * of grants; a grant opens only the routes of its own scope.
 */
export const RECORDING_GRANT_SCOPES = ["capture", "voice"] as const;
export type RecordingGrantScope = (typeof RECORDING_GRANT_SCOPES)[number];
/** The most tape entries one read returns; a reader asks again after the last one it has. */
export const TAPE_PAGE_MAX = 500;

/**
 * The caller's key for a create, an event or a screen, so sending it again answers what the first
 * did.
 */
export const callerKeySchema = z.string().min(1).max(100);

export const recordingViewSchema = z.strictObject({
  id: idSchema("recording"),
  threadId: idSchema("thread"),
  intent: z.string().nullable(),
  state: recordingStateSchema,
  createdAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
  /** The last tape position; the session folded from the tape through here is the recording's. */
  lastSeq: z.number().int().nonnegative(),
});
export type RecordingView = z.infer<typeof recordingViewSchema>;

/** The recordings on one chat. */
export const recordingListQuerySchema = z.object({
  threadId: idSchema("thread"),
});

/** A chat's recordings, oldest first, so each sits in the transcript where it was started. */
export const recordingListSchema = z.strictObject({
  recordings: z.array(recordingViewSchema),
});
export type RecordingList = z.infer<typeof recordingListSchema>;

/** One optional line of what the person is about to show; without it, Abacor asks out loud. */
export const createRecordingInputSchema = z.strictObject({
  threadId: idSchema("thread"),
  intent: z.string().trim().min(1).max(RECORDING_INTENT_MAX_CHARS).optional(),
  /**
   * The person wants Abacor's voice. A session starts with it only when voice is set up in this
   * region; otherwise it asks in text, and the tape's first entry says which.
   */
  voice: z.boolean().optional(),
  requestId: callerKeySchema,
});

/** The recording and the tape after a position: all of it from 0, or what a reader is missing. */
export const recordingTapeSchema = z.strictObject({
  recording: recordingViewSchema,
  entries: z.array(tapeEntrySchema).max(TAPE_PAGE_MAX),
  /** More entries follow the last one returned. */
  more: z.boolean(),
});
export type RecordingTape = z.infer<typeof recordingTapeSchema>;

export const tapeQuerySchema = z.object({
  after: queryInteger.pipe(z.int().nonnegative()),
});

/**
 * One frame of a recording's stream: the recording as it is and the tape entries after the last
 * frame's, in order. The first frame is the snapshot (the recording, and the entries after the
 * reader's `after`, a page at a time while `more`); later ones carry what landed since, or only the
 * recording when it changed. The envelope is versioned by `v`, as the chat stream's is.
 */
export const recordingTapeFrameSchema = z.strictObject({
  v: z.literal(1),
  kind: z.literal("tape"),
  recording: recordingViewSchema,
  entries: z.array(tapeEntrySchema).max(TAPE_PAGE_MAX),
  more: z.boolean(),
});
export type RecordingTapeFrame = z.infer<typeof recordingTapeFrameSchema>;

/** What the person did on the island. `key` is the island's own event ID. */
export const sendEventInputSchema = z.strictObject({
  key: callerKeySchema,
  event: personEventSchema,
});

export const sentEventSchema = z.strictObject({
  recording: recordingViewSchema,
  /** Where the event is on the tape; the same position when the key was sent before. */
  seq: z.number().int().positive(),
  duplicate: z.boolean(),
});
export type SentEvent = z.infer<typeof sentEventSchema>;

/*
 * What the chat's owner changes in a recording after it ended (ENG-1029), from the package view.
 * Each goes on the tape as an amendment; the answer is the package as it now folds. `key` is the
 * page's own ID for the change, so sending it again lands it once; the same key with another
 * change is CONFLICT.
 */

/** A moment on the recording's clock, in epoch milliseconds. */
const momentSchema = z.int().nonnegative();
const amendmentText = z.string().trim().min(1).max(INPUT_TEXT_MAX_CHARS);

/** A note at a moment: the person's own words. */
export const addNoteInputSchema = z.strictObject({
  key: callerKeySchema,
  moment: momentSchema,
  text: amendmentText,
});
export type AddNoteInput = z.infer<typeof addNoteInputSchema>;

/** A question's answer given again: the earlier one stays, and this one counts. */
export const correctAnswerInputSchema = z.strictObject({
  key: callerKeySchema,
  questionId: questionIdSchema,
  moment: momentSchema,
  text: amendmentText,
  /**
   * The answer the editor showed as standing (its `seq` in the package), or null for none. When
   * another stands by the time the correction lands, it is CONFLICT and nothing changes.
   */
  replaces: z.int().positive().nullable(),
});
export type CorrectAnswerInput = z.infer<typeof correctAnswerInputSchema>;

/**
 * One part to take out: a screen or an action by its ID, a line said or typed (or a note added
 * later) by its tape position, or everything in a stretch of the clock.
 */
export const removePartSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("screen"),
    id: idSchema("recordingScreen"),
  }),
  z.strictObject({ kind: z.literal("action"), id: idSchema("recordingTrace") }),
  z.strictObject({ kind: z.literal("said"), seq: z.int().positive() }),
  z
    .strictObject({
      kind: z.literal("range"),
      from: momentSchema,
      to: momentSchema,
    })
    .refine(({ from, to }) => to > from, "A stretch ends after it starts"),
]);
export type RemovePart = z.infer<typeof removePartSchema>;

export const removePartInputSchema = z.strictObject({
  key: callerKeySchema,
  part: removePartSchema,
});
export type RemovePartInput = z.infer<typeof removePartInputSchema>;
